import { HTMLThemeElement } from '@archetype-themes/custom-elements/theme-element';

const KlaviyoIdentifyKeys = ['$email', '$first_name', '$consent'];

const getKlaviyoValueForInput = (input) => {
  let tags = '';
  let value = '';

  const inputType = input.type;
  if (inputType === 'text' || inputType === 'email') {
    value = input.value;
  } else if (inputType === 'radio' || inputType === 'checkbox') {
    if (input.checked) {
      if (input.dataset.tags !== undefined) {
        tags = input.dataset.tags;
      } else {
        value = input.value;
      }
    }
  }

  return {
    tags,
    value,
  };
};

const getKlaviyoParameters = (step, stepInputs) => {
  const { klaviyoKey: stepKlaviyoKey, elements: elementGroup = [] } = step;
  const elements = elementGroup.flat();
  const klaviyoParams = [];

  if (stepKlaviyoKey) {
    const tags = [];
    let text = '';

    for (let i = 0; i < stepInputs.length; i += 1) {
      const input = stepInputs[i];
      const { tags: inputTags, value } = getKlaviyoValueForInput(input);
      if (inputTags.length) {
        tags.push(inputTags);
      } else {
        text = value;
      }
    }

    let klaviyoValue;

    if (tags.length) {
      klaviyoValue = tags.join('|');
    } else {
      klaviyoValue = text;
    }

    klaviyoParams.push({
      klaviyoKey: stepKlaviyoKey,
      klaviyoValue,
    });
  } else {
    elements.forEach((element) => {
      const elementId = element.id;
      const matchingInput = stepInputs.find(
        (stepInput) => stepInput.id === elementId
      );

      if (matchingInput && element.klaviyoKey) {
        const { value } = getKlaviyoValueForInput(matchingInput);

        klaviyoParams.push({
          klaviyoKey: element.klaviyoKey,
          klaviyoValue: value,
        });
      }
    });
  }

  return klaviyoParams;
};

class Quiz extends HTMLThemeElement {
  constructor() {
    super();

    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.onPreviousSubmit = this.onPreviousSubmit.bind(this);
    this.onIntroSubmit = this.onIntroSubmit.bind(this);
    this.onRadioChanged = this.onRadioChanged.bind(this);
    this.onSlideChanged = this.onSlideChanged.bind(this);
    this.onNextSubmit = this.onNextSubmit.bind(this);
    this.forceNextSubmit = this.forceNextSubmit.bind(this);
    this.showPlantResults = this.showPlantResults.bind(this);
  }

  connectedCallback() {
    this.activeIndex = 0;
    this.quizData = JSON.parse(
      this.querySelector('[data-quiz-model]').textContent
    );
    this.quizResults = {};
    this.redirectUrl = '';

    this._cacheElements();
    this._bindEvents();
    this._initializeFlickity();

    this.updateProgressBar(0);
    this.updateActiveStepClasses(0);
    this.updateActiveValidationRules(true);

    // Add a slight delay to ensure the transition is visible
    setTimeout(() => {
      this.stepsContainer.classList.add('visible');
    }, 500);
  }

  disconnectedCallback() {
    this._unbindEvents();
  }

  _cacheElements() {
    this.form = this.querySelector('form');
    this.stepsContainer = this.querySelector('.quiz-steps-container');
    this.steps = this.querySelectorAll('.quiz-step');
    this.nextBtn = this.querySelector('.js-quiz-next-btn');
    this.prevBtn = this.querySelector('.js-quiz-prev-btn');
    this.introBtn = this.querySelector('.js-quiz-intro-btn');
    this.progressBar = this.querySelector('.progress__bar');
    this.quizStepIndex = this.querySelector('.quiz-step-index');
    this.quizFooter = this.querySelector('.quiz-footer');
    this.onViewResultsButton = this.querySelector('#lr-quiz-view-results');
    this.radioArray = document.querySelectorAll(
      '.quiz-element-group input[type="radio"].form-element.checkbox'
    );
  }

  _bindEvents() {
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmitHandler);
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.onPreviousSubmit);
    }
    if (this.introBtn) {
      this.introBtn.addEventListener('click', this.onIntroSubmit);
    }
    if (this.radioArray.length > 0) {
      this.radioArray.forEach((elem) => {
        elem.addEventListener('change', this.onRadioChanged);
      });
    }
  }

  _unbindEvents() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmitHandler);
    }
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.onPreviousSubmit);
    }
    if (this.introBtn) {
      this.introBtn.removeEventListener('click', this.onIntroSubmit);
    }
    if (this.radioArray.length > 0) {
      this.radioArray.forEach((elem) => {
        elem.removeEventListener('change', this.onRadioChanged);
      });
    }
  }

  _initializeFlickity(retryCount = 0) {
    if (typeof window.Flickity === 'undefined') {
      if (retryCount > 20) {
        // Waited ~2 seconds, give up
        console.error('Flickity failed to load.');
        return;
      }
      setTimeout(() => this._initializeFlickity(retryCount + 1), 100);
      return;
    }

    this.slider = new Flickity(this.stepsContainer, {
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      pageDots: false,
      prevNextButtons: false,
      draggable: false,
    });

    this.slider.on('change', (index) => {
      this.onSlideChanged(index);
    });
  }

  createErrorDiv = (message) => {
    const div = document.createElement('div');
    div.setAttribute('data-type', 'validator-error');
    div.classList.add('error');
    div.innerText = message;

    return div;
  };

  triggerEventForSlider({ direction, index }) {
    const eventDetail = { sliderId: this.slider.id, direction, index };
    const sliderEvent = new CustomEvent('move-slider', { detail: eventDetail });

    document.dispatchEvent(sliderEvent);
  }

  validateCheckboxes() {
    // Ensure the active step element is correctly set
    this.activeStepEl = this.form.querySelector('.is-selected');
    if (!this.activeStepEl) {
      console.warn('No active step element found. Skipping validation.');
      return true; // Allow progression if no active step exists
    }

    // Get all input elements in the active step
    const inputEls = this.activeStepEl.querySelectorAll('input');
    const checkboxEls = this.activeStepEl.querySelectorAll(
      'input[type="checkbox"]'
    );
    const radioEls = this.activeStepEl.querySelectorAll('input[type="radio"]');
    const radioSelected = this.activeStepEl.querySelectorAll(
      'input[type="radio"]:checked'
    ).length;
    const anyCheckboxChecked = this.activeStepEl.querySelectorAll(
      'input[type="checkbox"]:checked'
    ).length;

    // Check if validation fails
    if (
      (checkboxEls.length &&
        inputEls.length === checkboxEls.length &&
        !anyCheckboxChecked) ||
      (radioEls.length && inputEls.length === radioEls.length && !radioSelected)
    ) {
      // Clear existing error messages
      const errorDivs = this.activeStepEl.querySelectorAll('div.error');
      errorDivs.forEach((elem) => {
        elem.remove();
      });

      // Create and append the error message
      const errorDiv = this.createErrorDiv(
        'At least one option must be selected'
      );
      const errorContainer = this.activeStepEl; // Attach to the same container as before
      if (errorContainer) {
        errorContainer.appendChild(errorDiv); // Append error message to the container
      } else {
        console.error('Error container not found in the active step.');
      }

      return false;
    }

    // Remove any existing error message if validation passes
    const errorDiv = this.activeStepEl.querySelector('div.error');
    if (errorDiv) {
      errorDiv.remove();
    }

    return true;
  }

  validateInputs() {
    // Ensure the active step element is set
    this.activeStepEl = this.form.querySelector('.is-selected');
    if (!this.activeStepEl) {
      console.warn('No active step element found. Skipping input validation.');
      return true; // Allow progression if no active step exists
    }

    // Remove previous error messages
    this.clearErrorMessages();

    let isValid = true;
    const activeInputs = this.activeStepEl.querySelectorAll(
      'input[required], input[type="email"], input[type="text"]'
    );

    activeInputs.forEach((input) => {
      // Skip hidden or disabled inputs
      if (input.offsetParent === null || input.disabled) return;

      if (input.type === 'email') {
        const email = input.value.trim();
        // Simple email regex
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!emailValid) {
          isValid = false;
          input.after(
            this.createErrorDiv('Please enter a valid email address.')
          );
        }
      } else {
        if (!input.value.trim()) {
          isValid = false;
          input.after(this.createErrorDiv('This field cannot be empty.'));
        }
      }
    });

    return isValid;
  }

  forceNextSubmit(skipValidation = false) {
    // Clear existing error messages
    this.clearErrorMessages();

    if (
      skipValidation ||
      (this.validateInputs() && this.validateCheckboxes())
    ) {
      this.onNextSubmit(null, true);
    }
  }

  clearErrorMessages() {
    const errorMessages = this.form.querySelectorAll(
      '[data-type="validator-error"]'
    );
    errorMessages.forEach((error) => error.remove());
  }

  updateProgressBar(stepIndex) {
    if (this.progressBar) {
      if (stepIndex === 0) {
        this.progressBar.style.width = '0%';
        this.progressBar.style.display = 'none'; // Hide progress bar
      } else {
        this.progressBar.style.display = 'block'; // Show progress bar
        const progressPercentage = (stepIndex / (this.steps.length - 1)) * 100;
        this.progressBar.style.width = `${progressPercentage}%`;
      }
    }

    if (this.quizStepIndex) {
      if (stepIndex === 0) {
        this.quizStepIndex.style.display = 'none'; // Hide step count
      } else {
        this.quizStepIndex.style.display = 'block'; // Show step count
        this.quizStepIndex.innerText = `${stepIndex} of ${
          this.steps.length - 1
        }`;
      }
    }
  }

  updateActiveStepClasses(activeIndex) {
    this.steps.forEach((step, index) => {
      if (index === activeIndex) {
        step.classList.add('active');
      } else {
        step.classList.remove('active');
      }
    });

    if (this.prevBtn) {
      if (activeIndex === 0) {
        this.quizFooter.classList.add('hidden');
      } else {
        this.quizFooter.classList.remove('hidden');
      }
    }
  }

  toggleButtons(toEnable, isLastStep) {
    // Enable or disable the previous and next buttons
    this.prevBtn[toEnable ? 'removeAttribute' : 'setAttribute'](
      'disabled',
      'true'
    );
    this.nextBtn[toEnable ? 'removeAttribute' : 'setAttribute'](
      'disabled',
      'true'
    );

    // Adjust button visibility for the last step
    if (isLastStep) {
      this.prevBtn.style.opacity = 0;
      this.nextBtn.style.opacity = 0;
    } else {
      this.prevBtn.style.opacity = 1;
      this.nextBtn.style.opacity = 1;
    }
  }

  toggleValidationRules(element, suspend) {
    let readAttr;
    let writeAttr;

    if (suspend) {
      readAttr = 'data-rule';
      writeAttr = 'data-rule-suspended';
    } else {
      readAttr = 'data-rule-suspended';
      writeAttr = 'data-rule';
    }

    const rules = element.getAttribute(readAttr);

    if (rules) {
      element.setAttribute(writeAttr, rules);
      element.removeAttribute(readAttr);
    }
  }

  updateActiveValidationRules(isInitialLoad) {
    // Suspend rules from all steps except the active one
    const allFormElements = this.form.querySelectorAll('.form-element');

    allFormElements.forEach((formEl) => {
      this.toggleValidationRules(formEl, true);
    });

    const activeStepFormElements = this.form.querySelectorAll(
      '.is-selected .form-element'
    );

    activeStepFormElements.forEach((formEl) => {
      this.toggleValidationRules(formEl, false);
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();
    this.forceNextSubmit();
  }

  onPreviousSubmit() {
    let nearestPrevQuestionIndex = -1;

    for (let i = this.activeIndex - 1; i >= 0; i--) {
      const step = this.quizData.steps[i];
      if (step.type === 'question') {
        nearestPrevQuestionIndex = i;
        break;
      }
    }

    if (nearestPrevQuestionIndex === -1) {
      return;
    }

    this.slider.select(nearestPrevQuestionIndex); // Move the slider
    this.activeIndex = nearestPrevQuestionIndex; // Synchronize activeIndex
  }

  onIntroSubmit() {
    if (this.activeIndex === 0) {
      // Skip validation for the intro step and move to the next step
      this.forceNextSubmit(true); // Pass `true` to skip validation
    } else {
      console.warn('Intro button clicked, but already past the intro step.');
    }
  }

  onRadioChanged(event) {
    if (this.submitting) {
      return;
    }

    this.submitting = true; // Prevent duplicate submissions
    setTimeout(() => {
      this.forceNextSubmit();
      this.submitting = false; // Reset flag after submission
    }, 250);
  }

  onSlideChanged(index) {
    if (index === this.activeIndex) {
      return;
    }

    this.activeIndex = index; // Update the active index
    this.updateProgressBar(index); // Update progress bar
    this.updateActiveStepClasses(index);
    this.updateActiveValidationRules();

    const isLastStep = index === this.steps.length - 1;
    this.toggleButtons(true, isLastStep);
  }

  onNextSubmit(err, res) {
    if (!this.validateInputs() || !this.validateCheckboxes()) {
      return;
    }

    const step = this.quizData.steps[this.activeIndex];
    const stepInputs = this.activeStepEl.querySelectorAll('input,select');

    // Extract Klaviyo parameters from the current step inputs
    const klaviyoParams = getKlaviyoParameters(step, [...stepInputs]);

    // Store the extracted parameters in the quiz results
    klaviyoParams.forEach(({ klaviyoKey, klaviyoValue }) => {
      this.quizResults[klaviyoKey] = klaviyoValue;
    });

    // Remove any existing error messages
    const errorDivs = this.activeStepEl.querySelectorAll('div.error');
    errorDivs.forEach((elem) => {
      elem.remove();
    });

    this.slider.select(this.activeIndex + 1); // Move to the next slide
    window.scrollTo(0, 0); // Scroll to the top of the page

    // Check if the current step is the last step
    if (step.type === 'end') {
      this.onViewResultsButton.setAttribute('disabled', true);

      // Collect tags from the quiz results
      const tags = this.getTagsFromResults();
      const tagsString = tags.join(',');

      // Construct the redirect URL with query parameters
      const utmSource = this.quizData.utm_source || 'Completed Plant Quiz';
      const utmMedium = this.quizData.utm_medium || 'quiz-page';
      this.redirectUrl = `${window.location.origin}${step.redirectUrl}`;
      this.redirectUrl += `?tags=${tagsString}&utm_source=${encodeURIComponent(
        utmSource
      )}&utm_medium=${encodeURIComponent(utmMedium)}`;

      if (this.quizData.resultsView && this.quizData.resultsView !== '') {
        this.redirectUrl += `&view=${this.quizData.resultsView}`;
      }

      // Trigger Klaviyo metric and email flow if applicable
      if (step.klaviyoMetric) {
        this.sendKlaviyoIdentifyRequest(step.klaviyoMetric, tagsString);
      } else {
        this.showPlantResults();
      }
    } else {
      // Log the step change to the data layer for analytics
      this.logStepChangeToDataLayer();
    }
  }

  showPlantResults() {
    if (this.redirectUrl) {
      window.location.href = this.redirectUrl;
    } else {
      console.warn('No redirect URL specified.');
    }
  }

  logStepChangeToDataLayer = () => {
    try {
      const currentStep = this.quizData.steps[this.activeIndex];
      const nextStep = this.quizData.steps[this.activeIndex + 1];

      const dataLayerEvent = {
        event: 'plant_quiz_step_change',
        currentStep: currentStep.heading,
        nextStep: nextStep?.heading || null,
        userInput: this.getDataLayerValueForStep(),
        _clear: true,
      };

      window.dataLayer.push(dataLayerEvent);
    } catch (err) {
      console.error('Error logging quiz data layer event', err);
    }
  };

  getDataLayerValueForStep = () => {
    const choices = [];
    const stepInputs = this.activeStepEl.querySelectorAll('input,select');

    stepInputs.forEach((input) => {
      const inputType = input.type;

      if (inputType === 'text' || inputType === 'email') {
        choices.push({
          title: input.name,
          value: input.value,
        });
      } else if (inputType === 'checkbox') {
        if (input.checked) {
          choices.push({
            title: input.name,
            value: input.value,
          });
        }
      } else if (inputType === 'radio') {
        if (input.checked) {
          choices.push({
            title: input.id,
            value: 'yes',
          });
        }
      }
    });

    return choices;
  };

  getTagsFromResults() {
    const tags = [];
    const resultKeys = Object.keys(this.quizResults);

    resultKeys.forEach((key) => {
      if (!KlaviyoIdentifyKeys.includes(key)) {
        tags.push(this.quizResults[key]);
      }
    });

    return tags;
  }

  async subscribeToKlaviyo(email) {
    try {
      const res = await fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=LedXK6',
        {
          method: 'POST',
          headers: {
            revision: '2025-07-15',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Plant Quiz',
                profile: {
                  data: {
                    "type": "profile",
                    "attributes": {
                      "email": email,
                      "subscriptions": {
                        "email": {
                          "marketing": {
                            "consent": "SUBSCRIBED"
                          }
                        }
                      }
                    },
                  }
                },
              },
              relationships: {
                list: {
                  data: {
                    id: "Ka499W", // Newsletter",
                    type: "list"
                  }
                }
              }

            },
          })
        }
      );

      if (!res.ok) {
        console.log(
          'Unsuccessful while subscribing to email',
          res.statusText,
          res.status
        );
      }
    } catch (err) {
      console.log('Error while subscribing to email');
    }
  }

  async sendKlaviyoIdentifyRequest(quizMetric, tagsString) {
    if (!window.klaviyo) {
      console.warn('Klaviyo is not available.');
      return;
    }

    const {
      $email: email,
      $first_name: firstName,
      $consent: consent,
    } = this.quizResults;

    if (!email || !firstName) {
      console.warn('Email or first name is missing in quiz results.');
      return;
    }

    const data = {
      email,
      ...(firstName ? { first_name: firstName } : {}),
    };

    try {
      window.klaviyo.push([
        'identify',
        data,
        async () => {
          if (consent) {
            await this.subscribeToKlaviyo(email);
          }
          this.sendKlaviyoMetric(quizMetric, tagsString);
          this.showPlantResults();
        },
      ]);
    } catch (error) {
      console.error('Error sending Klaviyo identify request:', error);
    }
  }

  async sendKlaviyoMetric(metric, value) {
    if (!window.klaviyo || !this.quizResults.$email) {
      console.warn(
        'Klaviyo is not available or email is missing in quiz results.'
      );
      return;
    }

    const data = {
      plant_quiz_results: value,
    };

    try {
      window.klaviyo.push(['track', metric, data]);
    } catch (error) {
      console.error('Error sending Klaviyo metric:', error);
    }
  }
}

customElements.define('lr-quiz', Quiz);